import * as utils from "@client/utils";
import {server} from "@reactivated";
import Decimal from "decimal.js";
import React from "react";

type Snapshot = server.manager.templates.WeeklySummaryEmail["clients"][number];

// An email: styles inline, no bundle. The text part is derived from this
// markup by the renderer.
const metricLines = (client: Snapshot) =>
    [client.primary_metric, client.secondary_metric, client.tertiary_metric]
        .filter((metric): metric is NonNullable<typeof metric> => metric != null)
        .map((metric) => {
            if (metric.value == null) {
                return `${metric.metric.label} was not logged`;
            }
            const isImperial = client.client_manager.client.profile.is_imperial;
            const format = (value: string) =>
                utils.formatDecimalAndUnit(
                    new Decimal(value).toDecimalPlaces(1),
                    metric.metric.type,
                    isImperial,
                );
            const current = format(metric.value);
            if (metric.previous != null && metric.delta != null) {
                return `${metric.metric.label} changed ${format(metric.delta)} from ${format(metric.previous)} to ${current}`;
            }
            return `${metric.metric.label} was ${current}`;
        });

const count = (
    client: Snapshot,
    test: (status: Snapshot["statuses"][number]) => boolean,
) => `${client.statuses.filter(test).length} / ${client.statuses.length} days`;

export const Template = (props: server.manager.templates.WeeklySummaryEmail) => (
    <div style={{fontFamily: "Helvetica, Arial, sans-serif", lineHeight: 1.5}}>
        <p>Dear {props.manager_name},</p>
        <p>Below is a weekly report of your clients.</p>
        {props.clients.map((client) => (
            <div key={client.client_manager.client.email}>
                <h3>
                    {client.client_manager.name} — {client.client_manager.client.email}
                </h3>
                <ul>
                    <li>Last seen {utils.formatDistanceToNow(client.last_seen)} ago</li>
                    <li>
                        Logged food {count(client, (status) => status.logged_entries)}
                    </li>
                    <li>
                        Worked out {count(client, (status) => status.logged_workout)}
                    </li>
                    <li>
                        Hit targets{" "}
                        {count(client, (status) => status.status == "success")}
                    </li>
                    <li>Weighed in {count(client, (status) => status.weighed_in)}</li>
                    {metricLines(client).map((line) => (
                        <li key={line}>{line}</li>
                    ))}
                </ul>
            </div>
        ))}
        <p>
            Have feedback or would like to see something else on this report? Reply to
            this email or <a href={props.contact_url}>contact us</a>!
        </p>
        <p>
            Best,
            <br />
            The Joy Team
        </p>
    </div>
);
